import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpCancelService } from 'app/_services/http-cancel.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

// When user moves page, cancel any pending http calls to the backend, which may delay the next page loading
// https://www.freakyjolly.com/angular-how-to-cancel-http-calls-on-router-change/
//
// .subscribes() will no longer happen, but watch out for .toPromise() which will run with undefined
// need to .catch() or wrap in a try/catch
//
// I've updated this again, using NavigationEnd instead of ActivationEnd and comparing the urls before cancelling.
// Some pages have the same route except for the :id component, and the Interceptor was stopping the refresh of the component
// so now it has an added exception: don't cancel if the route is the same.

@Injectable()
export class CancelHttpInterceptor implements HttpInterceptor {
  private previousUrl: string;

  constructor(
    private router: Router,
    private httpCancelService: HttpCancelService
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.removeRouteParams(this.router.routerState.snapshot.url)),
      distinctUntilChanged()
    ).subscribe(currentUrl => {
      if (currentUrl !== this.previousUrl) {
        this.httpCancelService.cancelPendingRequests();
      }
      this.previousUrl = currentUrl;
    });
  }

  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(req).pipe(takeUntil(this.httpCancelService.onCancelPendingRequests()))
  }

  // Removes all digits (id params) and query string parameters from a URL string, leaving only the base route path.
  private removeRouteParams(url: string): string {
    const semicolonIndex = url.indexOf(';');
    const questionIndex = url.indexOf('?');
    const endIndex = Math.min(
      semicolonIndex > -1 ? semicolonIndex : url.length,
      questionIndex > -1 ? questionIndex : url.length
    );
    return url.slice(0, endIndex).replace(/\d+/g, '');
  }
}
